import { useCurrentUserProfile } from "@/hooks/current-user-profile.hook";
import { listenToSignInAction, revalidateSession } from "@/lib/auth";
import UserNotAllowedPage from "@/modules/errors/user-not-allowed.page";
import { useAuthActions, useAuthTokenData } from "@/store/auth.store";
import { FC, ReactNode, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const authLoginUrl = import.meta.env.VITE_AUTH_APP_URL;
const currentUrl = window.location.href;

type ProtectedRouteProps = {
  children: ReactNode;
};

const ProtectedRoute: FC<ProtectedRouteProps> = (props) => {
  const location = useLocation();

  const [isRevalidatingSession, setIsRevalidatingSession] = useState(true);

  const { init: loadAuthTokens } = useAuthActions();

  const tokenData = useAuthTokenData();

  const tokenExpiration = tokenData?.expirationTime.getTime() || 0;

  const {
    data: currentUserProfile,
    isLoading,
    error,
  } = useCurrentUserProfile();

  useEffect(() => {
    loadAuthTokens();

    listenToSignInAction();

    revalidateSession().then(() => {
      setIsRevalidatingSession(false);
    });
  }, []);

  const isTokenExpired = Date.now() >= tokenExpiration * 1000;

  const userHasAccess = !isLoading && currentUserProfile?.companyId;

  const authAppUrl = `${authLoginUrl}/?originUrl=${encodeURIComponent(currentUrl)}`;

  if (isTokenExpired) {
    return <iframe src={authAppUrl} className="h-full w-full" />;
  }

  if (isLoading || isRevalidatingSession) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-[#262452]">
        <div className="loader" />
      </div>
    );
  }

  if (error) {
    throw error;
  }

  if (!userHasAccess && location.pathname !== "/welcome") {
    const currentUserEmail = tokenData?.email || "";
    return <UserNotAllowedPage email={currentUserEmail} />;
  }

  return props.children;
};

export default ProtectedRoute;
